<template>
  <div>

    <template v-if="!editing">
      <div class="row mb-2">
        <div class="col-md-6 position-relative">
          <input v-model="search" class="input form-control" placeholder="Buscar grupo...">
          <search-icon class="icon icon-within"/>
        </div>
        <div class="col-md-6 text-right">
          <button class="btn btn-outline-secondary mr-2" @click="goToUsers()">Ir para Usuários</button>
          <button class="btn btn-primary" @click="create()">Novo grupo</button>
        </div>
      </div>

      <p v-if="loading" class="text-center text-muted py-4">Carregando...</p>

      <div v-else class="row">
        <div v-for="group in filtered" :key="group.id" class="col-md-4 mb-2">
          <div class="card h-100 pointer" @click="edit(group)">
            <div class="card-body p-3">

              <div class="d-flex align-items-start justify-content-between">
                <strong class="small text-truncate">
                  <lock-icon v-if="group.system" class="icon-sm text-muted"/>
                  {{ group.name }}
                </strong>
                <span class="badge" :class="group.system ? 'badge-secondary' : 'badge-light'">
                  {{ group.system ? 'Sistema' : 'Personalizado' }}
                </span>
              </div>

              <p class="small text-muted mt-1 mb-2 description">{{ group.description }}</p>

              <p v-if="!group.all_companies" class="small mb-2" :title="allCnpjs(group)">
                <strong>Empresas:</strong>
                <span v-if="!group.cnpjs || group.cnpjs.length === 0" class="text-danger">nenhuma</span>
                <span v-else class="text-muted">{{ cnpjLabel(group) }}</span>
              </p>

              <div class="border-top pt-2 d-flex align-items-center justify-content-between">
                <small class="text-muted">
                  <a class="pointer" title="Ver os usuários deste grupo"
                     @click.stop="goToUsers(group.id)">{{ group.users_count || 0 }} usuário(s)</a>
                  <span class="mx-1">·</span>
                  {{ group.permissions_count }}/{{ group.permissions_total }} permissões
                  <template v-if="group.hidden_count > 0">
                    <span class="mx-1">·</span>
                    <span class="text-danger">{{ group.hidden_count }} oculto(s)</span>
                  </template>
                </small>
                <span>
                  <copy-icon class="icon-sm pointer" title="Duplicar" @click.stop="duplicate(group)"/>
                  <trash-2-icon v-if="!group.system" class="icon-sm pointer text-danger ml-2"
                                title="Excluir" @click.stop="askDelete(group)"/>
                </span>
              </div>

            </div>
          </div>
        </div>
      </div>

      <p v-if="!loading && filtered.length === 0" class="text-center text-muted py-4">
        Nenhum grupo encontrado
      </p>
    </template>

    <Form v-else
          :catalog="catalog"
          :group="editingGroup"
          @back="closeEditor()"
          @saved="onSaved"
          @see-users="goToUsers(editingGroup && editingGroup.id)"/>

    <Modal v-model="showDelete" width="sm">
      <span slot="header">Excluir grupo</span>
      <div>
        <p v-if="deleteTarget">
          <template v-if="deleteTarget.users_count > 0">
            "{{ deleteTarget.name }}" tem {{ deleteTarget.users_count }} usuário(s).
            Quem ficar sem nenhum grupo volta para "Membro".
          </template>
          <template v-else>
            Tem certeza que deseja excluir "{{ deleteTarget.name }}"?
          </template>
        </p>
        <div class="text-right">
          <button class="btn btn-outline-secondary mr-2" @click="showDelete = false">Cancelar</button>
          <button class="btn btn-danger" @click="confirmDelete()">Excluir</button>
        </div>
      </div>
    </Modal>

  </div>
</template>

<script>
  import Form from './Form'
  import Formatter from '@/models/Formatter'
  import GlobalLoading from '@/mixins/GlobalLoading'
  import Modal from '@/components/shares/specialTag/Modal'
  import { CopyIcon, LockIcon, SearchIcon, Trash2Icon } from 'vue-feather-icons'
  import { EMPTY_CATALOG } from '@/models/permissionCatalog'

  export default {
    components: { CopyIcon, Form, LockIcon, Modal, SearchIcon, Trash2Icon },
    computed: {
      filtered() {
        const term = this.search.trim().toLowerCase()
        if (!term) return this.groups

        return this.groups.filter(group =>
          group.name.toLowerCase().includes(term) ||
          (group.description || '').toLowerCase().includes(term))
      }
    },
    created() {
      this.loadCatalog()
      this.load()

      // ?group=<id> abre o editor direto — é o atalho vindo da aba Usuários.
      const id = this.$route.query.group
      if (id) this.openById(Number(id))
    },
    data: () => ({
      catalog: EMPTY_CATALOG,
      deleteTarget: null,
      editing: false,
      editingGroup: null,
      groups: [],
      loading: false,
      pendingGroupId: null,
      search: '',
      showDelete: false
    }),
    methods: {
      askDelete(group) {
        this.deleteTarget = group
        this.showDelete = true
      },
      closeEditor() {
        this.editing = false
        this.editingGroup = null
        this.clearRouteGroup()
        this.load()
      },
      clearRouteGroup() {
        if (!this.$route.query.group) return

        history.replaceState({}, null, this.$route.path + '?tab=permission_groups')
      },
      confirmDelete() {
        const group = this.deleteTarget
        this.openGlobalLoad()
        this.axios.delete(`permission_groups/${group.id}`)
          .then(() => this.$notify.add('Grupo excluído'))
          .then(() => this.load())
          .catch(e => this.$notify.addError(e))
          .finally(() => {
            this.showDelete = false
            this.closeGlobalLoad()
          })
      },
      create() {
        this.editingGroup = null
        this.editing = true
      },
      duplicate(group) {
        this.openGlobalLoad()
        this.axios.post(`permission_groups/${group.id}/duplicate`, {})
          .then(response => this.$notify.add(`"${response.data.name}" criado`))
          .then(() => this.load())
          .catch(e => this.$notify.addError(e))
          .finally(() => this.closeGlobalLoad())
      },
      allCnpjs(group) {
        return (group.cnpjs || []).map(cnpj => Formatter.cnpj(cnpj)).join(', ')
      },
      // O card é estreito: mostra os dois primeiros e conta o resto. A lista
      // inteira fica no title, e o grupo aberto tem a seleção completa.
      cnpjLabel(group) {
        const cnpjs = (group.cnpjs || []).map(cnpj => Formatter.cnpj(cnpj))
        if (cnpjs.length <= 2) return cnpjs.join(', ')

        return `${cnpjs.slice(0, 2).join(', ')} +${cnpjs.length - 2}`
      },
      edit(group) {
        this.editingGroup = group
        this.editing = true
      },
      goToUsers(groupId) {
        const query = groupId ? `?tab=users&group=${groupId}` : '?tab=users'
        history.replaceState({}, null, this.$route.path + query)
        this.$router.go(0)
      },
      load() {
        this.loading = true
        return this.axios.get('permission_groups', { params: { per_page: 200, meta: true } })
          .then(response => {
            this.groups = response.data || []
            this.openPending()
          })
          .catch(e => this.$notify.addError(e))
          .finally(() => this.loading = false)
      },
      loadCatalog() {
        this.axios.get('permission_groups/catalog')
          .then(response => this.catalog = response.data || EMPTY_CATALOG)
          .catch(e => this.$notify.addError(e))
      },
      onSaved() {
        this.closeEditor()
      },
      openById(id) {
        this.pendingGroupId = id
      },
      openPending() {
        if (!this.pendingGroupId) return

        const group = this.groups.find(current => current.id === this.pendingGroupId)
        this.pendingGroupId = null
        if (group) this.edit(group)
      }
    },
    mixins: [GlobalLoading],
    name: 'PermissionGroupsContainer'
  }
</script>

<style scoped>
  .pointer { cursor: pointer; }
  .icon-sm { width: 14px; height: 14px; }
  .description { min-height: 2rem; }
  .position-relative { position: relative; }
</style>
